import React, { useCallback, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { ICategoryDataSummaryModel } from "../../types/types";
import { getShortMonthName } from "../../utils/common";
import Box from "@mui/material/Box";
import {
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import {
  FullscreenExit as FullscreenExitIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import { useDataExplanation } from "../../hooks/useDataExplanation";
import { DataExplanationDialog } from "../Dialogs/DataExplanationDialog";
import theme from "../../styles/theme";
import {
  PIPELINE_DOT_COLOR,
  splitAnalyticsRows,
  useAnalyticsYears,
} from "../../hooks/usePipeline";

const UNDER_CONTRACTS_DOT = "+ Under Contracts";
const PIPELINE_DOT = "+ Pipeline";

const getOptimalXAxisConfig = (
  dataLength: number,
  timePeriod: string,
  isExpanded: boolean = false
) => {
  const baseConfig = {
    fontSize: 14,
    angle: 0,
    textAnchor: "middle" as const,
    height: 40,
    interval: 0,
  };

  const threshold = isExpanded
    ? {
        small: 12,
        medium: 24,
        large: 48,
        xlarge: 72,
      }
    : {
        small: 6,
        medium: 12,
        large: 18,
        xlarge: 36,
      };

  if (dataLength <= threshold.small) return baseConfig;

  if (dataLength <= threshold.medium) {
    return { ...baseConfig, interval: 1 };
  }

  if (dataLength <= threshold.large) {
    return {
      ...baseConfig,
      fontSize: isExpanded ? 13 : 12,
      angle: -30,
      textAnchor: "end" as const,
      height: 50,
      interval: isExpanded ? 1 : 1,
    };
  }

  if (dataLength <= threshold.xlarge) {
    return {
      ...baseConfig,
      fontSize: isExpanded ? 12 : 11,
      angle: -45,
      textAnchor: "end" as const,
      height: 60,
      interval: isExpanded
        ? Math.floor(dataLength / 16)
        : Math.floor(dataLength / 12),
    };
  }

  return {
    ...baseConfig,
    fontSize: isExpanded ? 11 : 10,
    angle: -45,
    textAnchor: "end" as const,
    height: 70,
    interval: isExpanded
      ? Math.floor(dataLength / 12)
      : Math.floor(dataLength / 8),
  };
};

const getCompactLabel = (
  displayPeriod: string,
  timePeriod: string,
  dataLength: number,
  isExpanded: boolean = false
): string => {
  // In expanded view, show full labels for more data points
  const threshold = isExpanded ? 24 : 12;
  if (dataLength <= threshold) return displayPeriod;

  if (timePeriod === "month") {
    // "2024 Jan" -> "Jan '24"
    const match = displayPeriod.match(/(\d{4})\s+(\w{3})/);
    if (match) return `${match[2]} '${match[1].slice(-2)}`;
  } else if (timePeriod === "quarter") {
    // "2024 Q1" -> "Q1 '24"
    const match = displayPeriod.match(/(\d{4})\s+(Q\d)/);
    if (match) return `${match[2]} '${match[1].slice(-2)}`;
  }

  return displayPeriod;
};

interface LineChartWrapperProps {
  category: string;
  variableId: number | undefined;
  selectedYearIds?: number[];
  mode: "count" | "amount" | "average";
  color?: string;
  yearText?: string | number;
  timePeriod: "quarter" | "month" | "year";
  /** Adds a "+ Under Contracts" dot per period and takes those out of the line */
  includeUnderContracts?: boolean;
  expandTrigger?: boolean;
}

export const LineChartWrapper: React.FC<LineChartWrapperProps> = ({
  category,
  variableId,
  selectedYearIds,
  mode,
  color,
  timePeriod,
  includeUnderContracts = false,
  expandTrigger,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { years: yearFolders, pipelineYearId } = useAnalyticsYears();
  const { categories } = useSelector((state: RootState) => state.analytics);
  const categoryData = categories[category]?.data;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    explainData,
    explanation,
    isLoading,
    isOpen,
    closeExplanation,
    dailyLimitInfo,
  } = useDataExplanation({
    onError: (error) => console.error("Data explanation error:", error),
  });

  React.useEffect(() => {
    if (expandTrigger) {
      setIsExpanded(true);
    }
  }, [expandTrigger]);

  const variableData = variableId
    ? categoryData?.filter((x) => x.id === String(variableId))
    : categoryData;

  // The line's rows and the "+ Under Contracts" / "+ Pipeline" dot rows
  const split = useMemo(
    () =>
      splitAnalyticsRows(variableData, {
        years: yearFolders,
        selectedYearIds,
        pipelineYearId,
        includeUnderContracts,
      }),
    [
      variableData,
      yearFolders,
      selectedYearIds,
      pipelineYearId,
      includeUnderContracts,
    ],
  );
  const years = split.chartYears;
  const isSingleYearSelected = split.isSingleYear;

  // Generate complete set of periods for selected years
  const allPeriods = useMemo(() => {
    if (timePeriod === "year" || !isSingleYearSelected) {
      if (timePeriod === "year") {
        return split.periodYearIds;
      } else if (timePeriod === "quarter") {
        // Generate all quarters for selected years
        const yearIds = split.periodYearIds;

        const periods: Array<{
          yearId: number;
          quarter: number;
          periodKey: string;
        }> = [];
        yearIds.forEach((yearId) => {
          for (let q = 1; q <= 4; q++) {
            const year = years.find((y) => y.id === yearId)?.year;
            if (year) {
              periods.push({
                yearId,
                quarter: q,
                periodKey: `${year} Q${q}`,
              });
            }
          }
        });
        return periods;
      } else if (timePeriod === "month") {
        // Generate all months for selected years
        const yearIds = split.periodYearIds;

        const periods: Array<{
          yearId: number;
          month: number;
          periodKey: string;
        }> = [];
        yearIds.forEach((yearId) => {
          for (let m = 1; m <= 12; m++) {
            const year = years.find((y) => y.id === yearId)?.year;
            if (year) {
              periods.push({
                yearId,
                month: m,
                periodKey: `${year} ${getShortMonthName(m)}`,
              });
            }
          }
        });
        return periods;
      }
    }

    return Array.from(
      { length: timePeriod === "month" ? 12 : 4 },
      (_, i) => i + 1
    );
  }, [timePeriod, isSingleYearSelected, split, years]);

  const groupRows = useCallback((rows?: ICategoryDataSummaryModel[]) => {
    if (!rows) return undefined;

    if (isSingleYearSelected && timePeriod !== "year") {
      return rows.reduce((acc, item) => {
        const groupKey = timePeriod === "quarter" ? item.quarter : item.month;
        const existing = acc.find(
          (i) => i[timePeriod] === groupKey && i.id === item.id
        );

        if (existing) {
          existing.totalCount =
            (existing.totalCount || 0) + (item.totalCount || 0);
          existing.totalAmount =
            (existing.totalAmount || 0) + (item.totalAmount || 0);
          existing.sbCount = (existing.sbCount || 0) + (item.sbCount || 0);
          existing.sbAmount = (existing.sbAmount || 0) + (item.sbAmount || 0);
        } else {
          acc.push({
            ...item,
            totalCount: item.totalCount || 0,
            totalAmount: item.totalAmount || 0,
            sbCount: item.sbCount || 0,
            sbAmount: item.sbAmount || 0,
            [timePeriod]: groupKey,
          });
        }
        return acc;
      }, [] as ICategoryDataSummaryModel[]);
    } else if (timePeriod === "quarter" || timePeriod === "month") {
      // Multiple years with quarters/months - need to aggregate by yearId and period
      return rows.reduce((acc, item) => {
        const periodValue =
          timePeriod === "quarter" ? item.quarter : item.month;
        const existing = acc.find(
          (i) =>
            i.yearId === item.yearId &&
            i[timePeriod] === periodValue &&
            i.id === item.id
        );

        if (existing) {
          existing.totalCount =
            (existing.totalCount || 0) + (item.totalCount || 0);
          existing.totalAmount =
            (existing.totalAmount || 0) + (item.totalAmount || 0);
          existing.sbCount = (existing.sbCount || 0) + (item.sbCount || 0);
          existing.sbAmount = (existing.sbAmount || 0) + (item.sbAmount || 0);
        } else {
          acc.push({
            ...item,
            totalCount: item.totalCount || 0,
            totalAmount: item.totalAmount || 0,
            sbCount: item.sbCount || 0,
            sbAmount: item.sbAmount || 0,
          });
        }
        return acc;
      }, [] as ICategoryDataSummaryModel[]);
    } else {
      // Year view - aggregate by yearId
      return rows.reduce((acc, item) => {
        const existing = acc.find(
          (i) => i.yearId === item.yearId && i.id === item.id
        );

        if (existing) {
          existing.totalCount =
            (existing.totalCount || 0) + (item.totalCount || 0);
          existing.totalAmount =
            (existing.totalAmount || 0) + (item.totalAmount || 0);
          existing.sbCount = (existing.sbCount || 0) + (item.sbCount || 0);
          existing.sbAmount = (existing.sbAmount || 0) + (item.sbAmount || 0);
        } else {
          acc.push({
            ...item,
            totalCount: item.totalCount || 0,
            totalAmount: item.totalAmount || 0,
            sbCount: item.sbCount || 0,
            sbAmount: item.sbAmount || 0,
          });
        }
        return acc;
      }, [] as ICategoryDataSummaryModel[]);
    }
  }, [isSingleYearSelected, timePeriod]);

  // Merge with empty periods
  const mergeRows = useCallback((groupedData?: ICategoryDataSummaryModel[]) => {
    if (isSingleYearSelected && timePeriod !== "year") {
      return (allPeriods as number[]).map((period) => {
        const existing = groupedData?.find((d) => d[timePeriod] === period);
        return (
          existing || {
            [timePeriod]: period,
            totalCount: 0,
            totalAmount: 0,
            name: variableData?.[0]?.name || "",
          }
        );
      });
    } else if (timePeriod === "quarter" || timePeriod === "month") {
      // Multiple years with quarters/months
      return (
        allPeriods as Array<{
          yearId: number;
          quarter?: number;
          month?: number;
          periodKey: string;
        }>
      ).map((period) => {
        const existing = groupedData?.find((d) => {
          const yearMatch = d.yearId === period.yearId;
          const periodMatch =
            timePeriod === "quarter"
              ? d.quarter === period.quarter
              : d.month === period.month;
          return yearMatch && periodMatch;
        });

        return (
          existing || {
            yearId: period.yearId,
            [timePeriod]:
              timePeriod === "quarter" ? period.quarter : period.month,
            totalCount: 0,
            totalAmount: 0,
            name: variableData?.[0]?.name || "",
            periodKey: period.periodKey,
          }
        );
      });
    } else {
      return (allPeriods as number[]).map((period) => {
        const existing = groupedData?.find((d) => d.yearId === period);
        return (
          existing || {
            yearId: period,
            totalCount: 0,
            totalAmount: 0,
            name: variableData?.[0]?.name || "",
          }
        );
      });
    }
  }, [allPeriods, isSingleYearSelected, timePeriod, variableData]);

  // The line per period, with the dot sets of the same period alongside
  const mergedData = useMemo(() => {
    const actual: any[] = mergeRows(groupRows(split.actual));
    const underContract: any[] = mergeRows(groupRows(split.underContract));
    const pipeline: any[] = mergeRows(groupRows(split.pipeline));
    return actual.map((item, i) => ({
      ...item,
      underContract: underContract[i],
      pipeline: pipeline[i],
    }));
  }, [split, groupRows, mergeRows]);

  const trendData = useMemo(() => {
    const result = mergedData
      ?.slice()
      ?.sort((a, b) => {
        if (timePeriod === "year" || isSingleYearSelected) {
          if (timePeriod === "year") {
            const yearA = years.find((x) => x.id === a.yearId)?.year;
            const yearB = years.find((x) => x.id === b.yearId)?.year;
            return (yearA || 0) - (yearB || 0);
          }
          return 0;
        } else {
          // Multi-year quarters/months: sort by year then by period
          const yearA = years.find((x) => x.id === a.yearId)?.year || 0;
          const yearB = years.find((x) => x.id === b.yearId)?.year || 0;
          if (yearA !== yearB) {
            return yearA - yearB;
          }
          const periodA =
            timePeriod === "quarter"
              ? (a as any).quarter || 0
              : (a as any).month || 0;
          const periodB =
            timePeriod === "quarter"
              ? (b as any).quarter || 0
              : (b as any).month || 0;
          return periodA - periodB;
        }
      })
      ?.map((item) => {
        const year = years.find((y) => y.id === item.yearId)?.year;
        const count = item.totalCount || 0;
        const amount = item.totalAmount || 0;
        // Average Price only accounts for Seller & Buyer transactions
        const sbCount = (item as ICategoryDataSummaryModel).sbCount || 0;
        const sbAmount = (item as ICategoryDataSummaryModel).sbAmount || 0;
        const average = sbCount > 0 ? sbAmount / sbCount : 0;

        // The plotted value of the line plus extra rows (the dots stack on the line)
        const plotted = (rows: ICategoryDataSummaryModel[]) => {
          const sum = (key: "totalCount" | "totalAmount" | "sbCount" | "sbAmount") =>
            rows.reduce((total, row) => total + (row?.[key] || 0), 0);
          if (mode === "count") return sum("totalCount");
          if (mode === "amount") return sum("totalAmount");
          return sum("sbCount") > 0 ? sum("sbAmount") / sum("sbCount") : 0;
        };
        const underContract = item.underContract;
        const pipeline = item.pipeline;
        // A year with no folder yet only has dots
        const noLine = split.isDotsOnlyYear(item.yearId);

        let displayPeriod: string | number = year || "";
        if (!isSingleYearSelected && timePeriod === "quarter") {
          displayPeriod = `${year} Q${(item as any).quarter}`;
        } else if (!isSingleYearSelected && timePeriod === "month") {
          // Use shorter format for multi-year monthly view: "23 Jan" instead of "2023 Jan"
          const shortYear = year ? (typeof year === "string" ? year : year.toString().slice(-2)) : "";
          const shortMonth = getShortMonthName((item as any).month || 1);
          displayPeriod = `${shortYear} ${shortMonth}`;
        } else if (isSingleYearSelected && timePeriod === "quarter") {
          displayPeriod = `Q${(item as any).quarter}`;
        } else if (isSingleYearSelected && timePeriod === "month") {
          displayPeriod = getShortMonthName((item as any).month || 1);
        }

        return {
          year,
          count: noLine ? null : count,
          amount: noLine ? null : amount,
          average: noLine ? null : average,
          withUnderContract:
            underContract?.totalCount > 0 ? plotted([item, underContract]) : null,
          withPipeline:
            pipeline?.totalCount > 0
              ? plotted([item, underContract, pipeline])
              : null,
          quarter: (item as any).quarter,
          month: (item as any).month,
          name: item.name,
          displayPeriod,
          periodKey: (item as any).periodKey || displayPeriod,
        };
      });

    return result;
  }, [mergedData, isSingleYearSelected, timePeriod, years, mode, split]);

  const handleExplainData = () => {
    const variableName = variableData?.[0]?.name || "selected variable";
    const selectedYearText =
      (selectedYearIds ?? []).length > 0
        ? selectedYearIds!
            .filter((id) => id !== pipelineYearId)
            .map((id) => years.find((y) => y.id === id)?.year)
            .join(", ")
        : "all years";

    explainData(
      trendData,
      "variable",
      mode,
      timePeriod,
      selectedYearText,
      category,
      variableName,
      undefined
    );
  };

  const ChartComponent = ({
    height = 450,
    showTitle = false,
    isExpanded = false,
  }: {
    height?: number;
    showTitle?: boolean;
    isExpanded?: boolean;
  }) => {
    const xAxisConfig = getOptimalXAxisConfig(
      trendData.length,
      timePeriod,
      isExpanded
    );

    return (
      <ResponsiveContainer width="100%" height={isMobile ? 350 : 450}>
        <LineChart
          data={trendData}
          margin={{
            top: showTitle ? 30 : 5,
            right: xAxisConfig.angle !== 0 ? (isExpanded ? 60 : 50) : 30,
            left: isMobile ? 1 : 10,
            bottom: xAxisConfig.height + (isExpanded ? 20 : 0),
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="displayPeriod"
            axisLine={false}
            tick={{
              fontSize: isMobile ? 12 : xAxisConfig.fontSize,
              fontFamily: "Segoe UI",
            }}
            tickLine={false}
            type="category"
            angle={isMobile ? -45 : xAxisConfig.angle}
            textAnchor={isMobile ? "end" : xAxisConfig.textAnchor}
            height={xAxisConfig.height}
            interval={xAxisConfig.interval}
            tickFormatter={(value) =>
              getCompactLabel(value, timePeriod, trendData.length, isExpanded)
            }
          />
          <YAxis
            allowDecimals={mode === "count" ? false : true}
            domain={mode === "count" ? [0, "auto"] : [0, "auto"]}
            tickFormatter={(value) => {
              if (mode === "amount" || mode === "average") {
                const absValue = Math.abs(value);
                if (absValue >= 1000000) {
                  return `$${(value / 1000000).toFixed(1)}M`;
                } else if (absValue >= 1000) {
                  return `$${(value / 1000).toFixed(1)}K`;
                } else {
                  return new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value);
                }
              } else if (mode === "count") {
                return value;
              }
              return value;
            }}
          />
          <Tooltip
            formatter={(value: number | string, name, props) => {
              const isDot = name === UNDER_CONTRACTS_DOT || name === PIPELINE_DOT;
              if (
                (name === "Volume" && mode === "amount") ||
                (name === "Average Price (S&B)" && mode === "average") ||
                (isDot && mode !== "count")
              ) {
                return [
                  new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value as number),
                  name,
                ];
              }

              if (
                isSingleYearSelected &&
                timePeriod === "month" &&
                props.payload &&
                props.payload.month
              ) {
                const monthValue = props.payload.month;
                return [
                  name === "Units" ||
                  name === "Volume" ||
                  name === "Average Price (S&B)" ||
                  isDot
                    ? value
                    : getShortMonthName(monthValue),
                  name,
                ];
              }

              return [value, name];
            }}
            labelFormatter={(label) => {
              return label;
            }}
          />
          <Legend
            verticalAlign="top"
            align="left"
            wrapperStyle={{
              fontSize: isMobile ? 10 : 13,
              paddingRight: 0,
              paddingTop: 0,
              paddingBottom: 5,
            }}
          />

          {mode === "count" && (
            <Line
              type="monotone"
              dataKey="count"
              stroke={color || "#8884d8"}
              strokeWidth={2}
              activeDot={{ r: 8 }}
              name="Units"
              connectNulls={false}
              dot={{ r: 4 }}
            />
          )}

          {mode === "amount" && (
            <Line
              type="monotone"
              dataKey="amount"
              stroke={color || "#82ca9d"}
              strokeWidth={2}
              name="Volume"
              connectNulls={false}
              dot={{ r: 4 }}
            />
          )}

          {mode === "average" && (
            <Line
              type="monotone"
              dataKey="average"
              stroke={color || "#ff7300"}
              strokeWidth={2}
              name="Average Price (S&B)"
              connectNulls={false}
              dot={{ r: 4 }}
            />
          )}

          {split.includeUnderContracts && (
            <Line
              dataKey="withUnderContract"
              stroke={theme.palette.warning.main}
              strokeWidth={0}
              dot={{ fill: theme.palette.warning.main, stroke: "none", r: 5 }}
              activeDot={{ fill: theme.palette.warning.main, r: 7 }}
              name={UNDER_CONTRACTS_DOT}
              connectNulls={false}
              legendType="circle"
            />
          )}

          {split.includePipeline && (
            <Line
              dataKey="withPipeline"
              stroke={PIPELINE_DOT_COLOR}
              strokeWidth={0}
              dot={{ fill: PIPELINE_DOT_COLOR, stroke: "none", r: 5 }}
              activeDot={{ fill: PIPELINE_DOT_COLOR, r: 7 }}
              name={PIPELINE_DOT}
              connectNulls={false}
              legendType="circle"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: -33,
            zIndex: 1,
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <MuiTooltip title="Get AI insights about this trend data">
            <IconButton
              size="small"
              onClick={handleExplainData}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>
        <ChartComponent isExpanded={false} />
      </Box>

      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "90vh",
            maxWidth: "none",
            margin: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <span>
            {category.charAt(0).toUpperCase() + category.slice(1)} -{" "}
            {variableData?.[0]?.name || "Variable"} Trend
          </span>
          <IconButton
            onClick={() => setIsExpanded(false)}
            size="small"
            sx={{ color: "grey.600" }}
          >
            <FullscreenExitIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          <ChartComponent
            height={window.innerHeight - 200}
            showTitle={false}
            isExpanded={true}
          />
        </DialogContent>
      </Dialog>

      <DataExplanationDialog
        open={isOpen}
        onClose={closeExplanation}
        explanation={explanation}
        isLoading={isLoading}
        title={`${
          category.charAt(0).toUpperCase() + category.slice(1)
        } Variable Trend Analysis`}
        dailyLimitInfo={dailyLimitInfo}
      />
    </>
  );
};
